import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import {
  Bot,
  Wallet,
  Gift,
  Cpu,
  ShieldCheck,
  Scale,
  Flame,
  CandlestickChart,
  CheckCircle2,
  TrendingUp,
  LineChart,
} from "lucide-react";

// Visual accent + icon per Live risk preset (keyed by preset id).
const PRESET_UI = {
  live_conservativo: { icon: ShieldCheck, accent: "emerald", tag: "Basso rischio" },
  live_bilanciato: { icon: Scale, accent: "amber", tag: "Consigliato" },
  live_aggressivo: { icon: Flame, accent: "rose", tag: "Alto rischio" },
};

const ACCENTS = {
  emerald: {
    icon: "text-emerald-300 bg-emerald-500/10 border border-emerald-500/30",
    badge: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
    ring: "ring-emerald-400/70 border-emerald-400/60",
  },
  amber: {
    icon: "text-amber-300 bg-amber-500/10 border border-amber-500/30",
    badge: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
    ring: "ring-amber-400/70 border-amber-400/60",
  },
  rose: {
    icon: "text-rose-300 bg-rose-500/10 border border-rose-500/30",
    badge: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
    ring: "ring-rose-400/70 border-rose-400/60",
  },
};

const FEATURES = [
  {
    icon: CandlestickChart,
    title: "Entra solo nel trend H1",
    desc: "Opera nella direzione del trend a 4 ore, confermato da 4 filtri (RSI, MACD, price action, volume). Niente trade nel caos laterale.",
  },
  {
    icon: TrendingUp,
    title: "Lascia correre i profitti",
    desc: "Stop dinamico (trailing 4×ATR) e take-profit ampio: pochi trade di qualità, si cavalcano i trend lunghi dell'oro.",
  },
  {
    icon: ShieldCheck,
    title: "Recupero drawdown SANO",
    desc: "In drawdown il bot aumenta TEMPORANEAMENTE il rischio sui trade in trend (Recovery Boost), poi torna normale. Zero martingala, zero grid, zero averaging.",
  },
];

export const AutonomousPanel = ({
  presets = [],
  activePresetId,
  onSelectPreset,
  capital,
  onCapitalChange,
  currency = "USD",
  onCurrencyChange,
  botName,
  onBotNameChange,
  bonusExcluded = 0,
  onBonusChange,
}) => {
  const sym = currency === "EUR" ? "€" : "$";
  const livePresets = presets.filter((p) => String(p.id).startsWith("live_"));

  return (
    <div className="space-y-4" data-testid="autonomous-panel">
      {/* Intro */}
      <div className="rounded-xl border border-[hsl(var(--primary))]/30 bg-card p-5 shadow-[var(--shadow-sm)]">
        <div className="flex items-center gap-2 flex-wrap">
          <Cpu className="h-5 w-5 text-[hsl(var(--primary))]" />
          <span className="font-semibold tracking-[-0.01em] uppercase">Modalità Live 1H</span>
          <Badge
            variant="secondary"
            className="text-[10px] md:text-xs font-mono bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30"
          >
            consigliata
          </Badge>
        </div>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          &gt; Strategia <span className="text-[hsl(var(--primary))]">trend-following</span> pulita, validata out-of-sample su
          dati reali XAU/USD. Scegli un profilo di rischio, imposta il capitale e genera il bot.
          <span className="text-foreground"> Niente martingala, niente grid.</span>
        </p>
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200/90 leading-relaxed">
          <LineChart className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" />
          <span data-testid="h1-advisory">
            Importante: attacca il bot a un grafico <span className="font-semibold text-amber-200">XAUUSD a 1 ORA (H1)</span>.
            I risultati sono ottimizzati per questo timeframe; su M15 i costi erodono il vantaggio.
          </span>
        </div>
      </div>

      {/* Risk preset selection */}
      <div className="rounded-xl border border-border bg-card p-5 shadow-[var(--shadow-sm)]" data-testid="live-preset-card">
        <div className="flex items-center gap-2 mb-1">
          <Scale className="h-4 w-4 text-[hsl(var(--primary))]" />
          <span className="text-sm font-semibold uppercase tracking-wide">Profilo di rischio</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Più rischio = recupero più veloce ma drawdown più profondo. Numeri da backtest reale 2.5 anni (costi inclusi).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {livePresets.map((p) => {
            const ui = PRESET_UI[p.id] || PRESET_UI.live_bilanciato;
            const a = ACCENTS[ui.accent];
            const Icon = ui.icon;
            const active = activePresetId === p.id;
            const risk = p.config?.regimeTrendRiskPercent ?? p.config?.riskPerTradePercent;
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`live-preset-${p.id}`}
                onClick={() => onSelectPreset && onSelectPreset(p)}
                className={cn(
                  "text-left rounded-lg border bg-background/40 p-3.5 transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2",
                  active ? cn("ring-2", a.ring) : "border-border hover:border-[hsl(var(--primary))]/40"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className={cn("flex h-9 w-9 items-center justify-center rounded-md", a.icon)}>
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <Badge variant="secondary" className={cn("text-[10px] md:text-xs font-mono", a.badge)}>{ui.tag}</Badge>
                </div>
                <div className="mt-2.5 font-semibold text-sm tracking-[-0.01em]">{p.name}</div>
                <div className="mt-1 flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                  <span>Rischio {risk}%</span>
                  {active && <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />}
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed line-clamp-4">{p.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Capital + identity */}
      <div className="rounded-xl border border-border bg-card p-5 shadow-[var(--shadow-sm)]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Label htmlFor="auto-capital" className="text-sm font-medium flex items-center gap-2">
            <Wallet className="h-4 w-4 text-[hsl(var(--primary))]" /> Capitale iniziale ({sym})
          </Label>
          <ToggleGroup
            type="single"
            value={currency}
            onValueChange={(v) => v && onCurrencyChange && onCurrencyChange(v)}
            data-testid="currency-toggle"
            className="border border-border rounded-md p-0.5"
          >
            <ToggleGroupItem value="USD" data-testid="currency-usd" className="h-7 px-3 text-xs font-mono data-[state=on]:bg-[hsl(var(--primary))] data-[state=on]:text-[hsl(var(--primary-foreground))]">
              $ USD
            </ToggleGroupItem>
            <ToggleGroupItem value="EUR" data-testid="currency-eur" className="h-7 px-3 text-xs font-mono data-[state=on]:bg-[hsl(var(--primary))] data-[state=on]:text-[hsl(var(--primary-foreground))]">
              € EUR
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <span className="text-2xl font-bold text-[hsl(var(--primary))] font-mono">{sym}</span>
          <Input
            id="auto-capital"
            data-testid="auto-capital-input"
            type="number"
            inputMode="decimal"
            min={100}
            step={100}
            className="font-mono text-2xl h-14 max-w-[260px] tracking-[-0.02em]"
            value={capital}
            onChange={(e) => onCapitalChange(e.target.value)}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
          Capitale di riferimento usato per dimensionare il rischio per trade e calcolare il drawdown. Una volta
          installato, il pannello mostra la valuta reale del conto cTrader.
        </p>

        <div className="mt-4 pt-4 border-t border-border/70">
          <Label htmlFor="auto-bonus" className="text-sm font-medium flex items-center gap-2">
            <Gift className="h-4 w-4 text-[hsl(var(--primary))]" /> Bonus / credito da escludere ({sym})
          </Label>
          <Input
            id="auto-bonus"
            data-testid="auto-bonus-input"
            type="number"
            inputMode="decimal"
            min={0}
            step={100}
            className="mt-1.5 font-mono max-w-[260px]"
            value={bonusExcluded}
            onChange={(e) => onBonusChange && onBonusChange(e.target.value)}
            placeholder="0"
          />
          <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
            Se il tuo conto include un <span className="text-foreground">bonus non prelevabile</span> conteggiato
            nell'equity (es. 2497), inseriscilo qui: il bot lo <span className="text-[hsl(var(--primary))]">sottrae da equity e
            saldo</span> per calcolare il drawdown reale. Lascia 0 se non hai bonus.
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-border/70">
          <Label htmlFor="auto-botname" className="text-sm font-medium flex items-center gap-2">
            <Bot className="h-4 w-4 text-[hsl(var(--primary))]" /> Nome del Bot
          </Label>
          <Input
            id="auto-botname"
            data-testid="auto-botname-input"
            className="mt-1.5"
            value={botName || ""}
            onChange={(e) => onBotNameChange(e.target.value)}
            placeholder="ORACLE Gold Live 1H"
          />
        </div>
      </div>

      {/* What the bot does */}
      <div className="rounded-xl border border-border bg-card p-5 shadow-[var(--shadow-sm)]">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" />
          <span className="text-sm font-semibold uppercase tracking-wide">Come opera il bot</span>
        </div>
        <div className="space-y-2">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="flex items-start gap-3 rounded-md border border-border bg-background/40 p-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-md shrink-0 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm tracking-[-0.01em]">{f.title}</div>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
          &gt; Vuoi regolare ogni singolo parametro (filtri, ATR, soglie del Recovery Boost, ecc.)? Passa alla scheda
          <span className="text-[hsl(var(--primary))]"> Avanzata (Esperti)</span>.
        </p>
      </div>
    </div>
  );
};
